// hooks/useLibrary.ts - Fixed version
import { useEffect, useRef, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { getDoc, updateDoc, Timestamp, serverTimestamp } from 'firebase/firestore';
import { FileUploadService } from '../services/fileUploadService';
import { deleteProject, saveUploadProject, saveYouTubeProject } from '../services/projectService';
import { createSharedSession } from '../services/sharedSessionService';
import { incrementLibraryCount } from '../services/authService';
import { fetchYouTubeVideoMetadata, getDefaultYouTubeThumbnailUrl } from '../services/youtubeMetadataService';
import {
  VideoState,
  SegmentState,
  SHARE_LIMITS,
  LibraryItemCollection,
  NormalizedLibraryProject,
  YouTubeProjectSource
} from '../types';
import { UserProfile } from '../services/authService';
import { userVideoDocRef } from '../utils';
import { FREE_UPLOAD_PROJECT_LIMIT, SHOW_UPLOAD_SLOT_LIMIT_UI } from '../services/uploadQuotaPolicy';
import { getCurrentPlatformCapabilities } from '../../platform/platformCapabilities';
import {
  getLibraryProjectStoragePath,
  isGuestLibraryProject,
  normalizeSavedPracticeProject,
  updateLibraryProjectSegments
} from '../services/videoLibrary';
import {
  deleteGuestLibraryRecord,
  saveGuestUploadProject,
  saveGuestYouTubeProject
} from '../services/guestLibraryService';
import { migrateGuestLibraryToAccount } from '../services/guestLibraryMigration';

interface UseLibraryProps {
  isAuthenticated: boolean;
  userProfile: UserProfile | null;
  refreshProfile: () => Promise<void>;
}

type SaveToLibraryOptions = {
  auto?: boolean;
  autoSaveKey?: string;
};

type SaveYouTubeProjectOptions = SaveToLibraryOptions;

export const useLibrary = ({ 
  isAuthenticated, 
  userProfile, 
  refreshProfile 
  }: UseLibraryProps) => {
  const [currentVideoId, setCurrentVideoId] = useState<string | null>(null);
  const [currentStoragePath, setCurrentStoragePath] = useState<string | null>(null);
  const [isSavedToLibrary, setIsSavedToLibrary] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [shareExpiresAt, setShareExpiresAt] = useState<Date | null>(null);
  const [loadedFromLibraryId, setLoadedFromLibraryId] = useState<string | null>(null);
  const [loadedLibraryProject, setLoadedLibraryProject] = useState<NormalizedLibraryProject | null>(null);
  const [videoTitle, setVideoTitle] = useState<string>('');
  const [hasActiveShare, setHasActiveShare] = useState(false);
  // Auto-save status for both IndexedDB guest saves and Firebase account saves.
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const activeAutoSaveKeyRef = useRef<string | null>(null);
  const migrationAttemptedForUserRef = useRef(new Set<string>());
  const isInFullscreen = () => typeof document !== 'undefined' && !!document.fullscreenElement;
  const sharingEnabled = getCurrentPlatformCapabilities().canExposeSharing;

  /**
   * Check if video already has an active share
   */
  const checkExistingShare = async (videoId: string): Promise<{
    hasShare: boolean;
    shareUrl?: string;
    expiresAt?: Date;
  }> => {
    if (!sharingEnabled) return { hasShare: false };

    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) return { hasShare: false };

      const docRef = userVideoDocRef(user.uid, videoId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) return { hasShare: false };
      
      const data = docSnap.data();
      
      // Check if there's an active share
      if (data.currentShareId && data.currentShareExpiresAt) {
        const expiresAt = data.currentShareExpiresAt.toDate();
        const now = new Date();
        
        // If share is still valid
        if (expiresAt > now) {
          return {
            hasShare: true,
            shareUrl: data.currentShareUrl,
            expiresAt: expiresAt
          };
        } else {
          // Share expired, clear it
          await updateDoc(docRef, {
            currentShareId: null,
            currentShareUrl: null,
            currentShareExpiresAt: null,
            updatedAt: serverTimestamp()
          });
        }
      }
      
      return { hasShare: false };
    } catch (error) {
      console.error('Error checking existing share:', error);
      return { hasShare: false };
    }
  };

  /**
   * Save video to library (uses up a slot)
   */
  const handleSaveToLibrary = async (
    originalVideoFile: File | null,
    videoState: VideoState,
    segmentState: SegmentState,
    options: SaveToLibraryOptions = {}
  ) => {
    const { auto = false, autoSaveKey } = options;
    // `auto` is the save-on-drop path: it must tolerate a video
    // with no segments yet (drop first, segment after), stay silent on success,
    // and track progress through autoSaveStatus instead of alerts.
    if (!originalVideoFile) {
      // Demo videos have no underlying file and can't be uploaded.
      if (!auto) alert('Demo videos cannot be saved. Please upload your own video file.');
      return;
    }

    if (!auto && segmentState.segments.length === 0) {
      alert('Please create at least one segment before saving.');
      return;
    }

    // Note: authoritative upload quota check happens inside saveUploadProject
    // via the source-aware project save quota decision.

    const authUser = isAuthenticated ? getAuth().currentUser : null;
    if (isAuthenticated && !authUser) {
      if (!auto) alert('Please sign in to save videos to your library.');
      return;
    }

    const thisAutoSaveKey = auto ? (autoSaveKey || `${Date.now()}`) : null;
    const isStaleAutoSave = () => (
      auto &&
      thisAutoSaveKey !== null &&
      activeAutoSaveKeyRef.current !== thisAutoSaveKey
    );

    if (auto) {
      activeAutoSaveKeyRef.current = thisAutoSaveKey;
      setAutoSaveStatus('saving');
    }

    try {
      console.log('🟡 Saving video to library...');

      const settings = FileUploadService.createSettingsFromState(videoState, segmentState);
      const savedProject = authUser
        ? await saveUploadProject(
            originalVideoFile,
            videoState.videoFileName,
            segmentState.segments,
            settings
          ).then(({ id, project }) => normalizeSavedPracticeProject(id, project))
        : await saveGuestUploadProject(
            originalVideoFile,
            videoState.videoFileName,
            segmentState.segments,
            settings
          );
      const videoId = savedProject.id;

      if (isStaleAutoSave()) {
        try {
          if (authUser) {
            await deleteProject(videoId);
            await incrementLibraryCount(authUser.uid);
            await refreshProfile();
          } else {
            await deleteGuestLibraryRecord(videoId);
          }
        } catch (cleanupError) {
          console.warn('⚠️ Failed to clean up stale auto-save:', cleanupError);
        }
        return;
      }

      setCurrentVideoId(videoId);
      setCurrentStoragePath(getLibraryProjectStoragePath(savedProject));
      setIsSavedToLibrary(true);
      setLoadedFromLibraryId(videoId);
      setLoadedLibraryProject(savedProject);
      setVideoTitle(videoState.videoFileName);
      
      // Sync library count after the new upload-backed project is written.
      if (authUser) {
        await incrementLibraryCount(authUser.uid);
        await refreshProfile();
      }

      if (isStaleAutoSave()) {
        try {
          if (authUser) {
            await deleteProject(videoId);
            await incrementLibraryCount(authUser.uid);
            await refreshProfile();
          } else {
            await deleteGuestLibraryRecord(videoId);
          }
        } catch (cleanupError) {
          console.warn('⚠️ Failed to clean up stale auto-save:', cleanupError);
        }
        return;
      }
      
      console.log('🟢 Video saved to library:', videoId);

      if (auto) {
        // Auto-save on drop: no alert, just record status internally.
        setAutoSaveStatus('saved');
        return;
      }

      if (!authUser) {
        if (!isInFullscreen()) {
          alert('Saved on this device. Sign in later to back it up and use it on other devices.');
        }
        return;
      }

      const nextLibraryCount = (userProfile?.libraryCount || 0) + 1;
      const slotsTotal = userProfile?.isPremium ? '∞' : String(FREE_UPLOAD_PROJECT_LIMIT);
      if (!SHOW_UPLOAD_SLOT_LIMIT_UI) {
        if (isInFullscreen()) {
          console.log('Video saved to library.');
        } else {
          alert('Video saved to your Croop library!');
        }
      } else if (isInFullscreen()) {
        console.log(`Video saved to library (${nextLibraryCount}/${slotsTotal} Croop Free slots used).`);
      } else {
        alert(`Video saved to your Croop library!\n\n${nextLibraryCount}/${slotsTotal} Free upload slots used`);
      }
    } catch (error) {
      const err = error as any;
      if (auto) {
        // Keep the video usable; surface only quota failures to the user (and
        // the QuotaExceeded message, which is the one thing worth an alert).
        setAutoSaveStatus('error');
        console.error('🔴 Auto-save failed:', err?.message || error);
        if (err?.name === 'QuotaExceededError') alert(err.message);
        return;
      }
      if (err?.name === 'QuotaExceededError') {
        alert(err.message);
      } else {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        console.error('🔴 Save failed:', errorMessage);
        alert(`Failed to save video: ${errorMessage}`);
      }
    }
  };

  /**
   * Save a YouTube practice session to the library (no upload slot consumed).
   */
  const handleSaveYouTubeProject = async (
    youtubeSource: { videoId: string; startTime?: number },
    videoState: VideoState,
    segmentState: SegmentState,
    options: SaveYouTubeProjectOptions = {}
  ) => {
    const { auto = false, autoSaveKey } = options;

    if (!auto && segmentState.segments.length === 0) {
      alert('Please create at least one segment before saving.');
      return;
    }

    const thisAutoSaveKey = auto ? (autoSaveKey || `${Date.now()}`) : null;
    const isStaleAutoSave = () => (
      auto &&
      thisAutoSaveKey !== null &&
      activeAutoSaveKeyRef.current !== thisAutoSaveKey
    );

    if (auto) {
      activeAutoSaveKeyRef.current = thisAutoSaveKey;
      setAutoSaveStatus('saving');
    }

    try {
      console.log('🟡 Saving YouTube practice session to library...');
      const canonicalUrl = `https://www.youtube.com/watch?v=${youtubeSource.videoId}`;
      const metadata = await fetchYouTubeVideoMetadata(youtubeSource.videoId);
      const source: YouTubeProjectSource = {
        type: 'youtube',
        videoId: youtubeSource.videoId,
        canonicalUrl,
        thumbnailUrl: metadata?.thumbnailUrl || getDefaultYouTubeThumbnailUrl(youtubeSource.videoId)
      };
      if (youtubeSource.startTime !== undefined) {
        source.startTime = youtubeSource.startTime;
      }
      if (metadata?.title) {
        source.videoTitle = metadata.title;
      }
      const title = metadata?.title || videoState.videoFileName || source.canonicalUrl;
      const settings = FileUploadService.createSettingsFromState(videoState, segmentState);
      const savedProject = isAuthenticated
        ? await saveYouTubeProject(
            source,
            title,
            segmentState.segments,
            settings
          ).then(({ id, project }) => normalizeSavedPracticeProject(id, project))
        : await saveGuestYouTubeProject(
            source,
            title,
            segmentState.segments,
            settings
          );
      const videoId = savedProject.id;

      if (isStaleAutoSave()) {
        try {
          if (isAuthenticated) await deleteProject(videoId);
          else await deleteGuestLibraryRecord(videoId);
        } catch (cleanupError) {
          console.warn('⚠️ Failed to clean up stale YouTube auto-save:', cleanupError);
        }
        return;
      }

      setCurrentVideoId(videoId);
      setCurrentStoragePath(null);
      setIsSavedToLibrary(true);
      setLoadedFromLibraryId(videoId);
      setLoadedLibraryProject(savedProject);
      setVideoTitle(title);
      if (isAuthenticated) await refreshProfile();

      if (isStaleAutoSave()) {
        try {
          await deleteProject(videoId);
        } catch (cleanupError) {
          console.warn('⚠️ Failed to clean up stale YouTube auto-save:', cleanupError);
        }
        return;
      }

      console.log('🟢 YouTube session saved to library:', videoId);
      if (auto) {
        setAutoSaveStatus('saved');
        return;
      }
      if (!isAuthenticated) {
        if (!isInFullscreen()) {
          alert('Saved on this device. Sign in later to back it up and use it on other devices.');
        }
        return;
      }
      if (!isInFullscreen()) {
        alert('YouTube practice session saved to your Croop library!');
      }
    } catch (error) {
      const err = error as any;
      if (err?.name === 'QuotaExceededError') {
        if (auto) {
          setAutoSaveStatus('error');
          console.error('🔴 YouTube auto-save failed:', err.message);
          return;
        }
        alert(err.message);
      } else {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        console.error('🔴 YouTube save failed:', errorMessage);
        if (auto) {
          setAutoSaveStatus('error');
          return;
        }
        alert(`Failed to save: ${errorMessage}`);
      }
    }
  };

  /**
   * Update segments for the currently loaded library video
   */
  const handleUpdateSegments = async (
    videoState: VideoState,
    segmentState: SegmentState,
    options: { auto?: boolean } = {}
  ) => {
    const { auto = false } = options;
    // `auto` is the debounced background persist that runs as segments change on
    // an already-saved project: silent, and tracked through autoSaveStatus.
    if (!loadedLibraryProject) {
      if (!auto) alert('This video is not from your library. Save it first to create a library entry.');
      return;
    }

    if (!isAuthenticated && !isGuestLibraryProject(loadedLibraryProject)) {
      if (!auto) alert('Sign in again to update this cloud library item.');
      return;
    }

    if (!auto && segmentState.segments.length === 0) {
      alert('Please create at least one segment before updating.');
      return;
    }

    if (auto) setAutoSaveStatus('saving');

    try {
      console.log('🟡 Updating library segments...');

      const settings = FileUploadService.createSettingsFromState(videoState, segmentState);

      await updateLibraryProjectSegments(loadedLibraryProject, segmentState.segments, settings);
      setLoadedLibraryProject(prev => prev ? {
        ...prev,
        segments: segmentState.segments,
        settings
      } : prev);

      console.log('🟢 Library segments updated successfully');
      if (auto) {
        setAutoSaveStatus('saved');
        return;
      }
      if (isInFullscreen()) {
        console.log('Segments updated successfully. Changes saved to library.');
      } else {
        alert('Segments updated successfully! Your changes are saved to the library.');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('🔴 Update failed:', errorMessage);
      if (auto) {
        setAutoSaveStatus('error');
        return;
      }
      alert(`Failed to update segments: ${errorMessage}`);
    }
  };

  /**
   * Share saved video (reuse existing or create new)
   */
  const handleShareSession = async (
    videoState: VideoState,
    segmentState: SegmentState
  ) => {
    if (!sharingEnabled) return;

    if (!currentVideoId) {
      alert('Please save the video to your library first.');
      return;
    }

    if (!currentStoragePath) {
      alert('Video storage path not found. Please try saving the video again.');
      return;
    }

    if (!userProfile) {
      alert('Please sign in to share videos.');
      return;
    }

    try {
      // First check if there's already an active share
      const existing = await checkExistingShare(currentVideoId);
      
      if (existing.hasShare && existing.shareUrl && existing.expiresAt) {
        // Reuse existing share
        console.log('🟡 Reusing existing share link');
        setShareUrl(existing.shareUrl);
        setShareExpiresAt(existing.expiresAt);
        setShowShareModal(true);
        setHasActiveShare(true);
        return;
      }

      // No active share, create new one
      console.log('🟡 Creating new shareable session...');
      console.log('Storage path:', currentStoragePath);
      
      const settings = FileUploadService.createSettingsFromState(videoState, segmentState);
      
      // Create shareable session with storage path
      const result = await createSharedSession(
        currentVideoId,
        currentStoragePath, // Pass the full storage path
        videoTitle || videoState.videoFileName,
        segmentState.segments,
        settings,
        userProfile.uid
      );
      
      // Save share info to video metadata
      const auth = getAuth();
      if (auth.currentUser) {
        const docRef = userVideoDocRef(auth.currentUser.uid, currentVideoId);
        await updateDoc(docRef, {
          currentShareId: result.shareId,
          currentShareUrl: result.shareUrl,
          currentShareExpiresAt: Timestamp.fromDate(result.expiresAt),
          updatedAt: serverTimestamp()
        });
      }
      
      setShareUrl(result.shareUrl);
      setShareExpiresAt(result.expiresAt);
      setShowShareModal(true);
      setHasActiveShare(true);
      
      console.log('🟢 Share link created:', result.shareUrl);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('🔴 Share failed:', errorMessage);
      alert(`Failed to create share link: ${errorMessage}`);
    }
  };

  /**
   * Create a new share (replacing existing one)
   */
  const handleCreateNewShare = async (
    videoState: VideoState,
    segmentState: SegmentState
  ) => {
    if (!sharingEnabled) return;

    if (!currentVideoId || !userProfile || !currentStoragePath) return;

    try {
      console.log('🟡 Creating NEW shareable session...');
      
      const settings = FileUploadService.createSettingsFromState(videoState, segmentState);
      
      // Create new shareable session
      const result = await createSharedSession(
        currentVideoId,
        currentStoragePath,
        videoTitle || videoState.videoFileName,
        segmentState.segments,
        settings,
        userProfile.uid
      );
      
      // Update video metadata with new share info
      const auth = getAuth();
      if (auth.currentUser) {
        const docRef = userVideoDocRef(auth.currentUser.uid, currentVideoId);
        await updateDoc(docRef, {
          currentShareId: result.shareId,
          currentShareUrl: result.shareUrl,
          currentShareExpiresAt: Timestamp.fromDate(result.expiresAt),
          updatedAt: serverTimestamp()
        });
      }
      
      setShareUrl(result.shareUrl);
      setShareExpiresAt(result.expiresAt);
      setHasActiveShare(true);
      
      console.log('🟢 New share link created:', result.shareUrl);
      alert('New share link created successfully!');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('🔴 Share failed:', errorMessage);
      alert(`Failed to create share link: ${errorMessage}`);
    }
  };

  /**
   * Mark that current video was loaded from library
   */
  const setLoadedFromLibrary = async (
    projectOrId: NormalizedLibraryProject | string,
    storagePath = '',
    title?: string,
    collection?: LibraryItemCollection
  ) => {
    const project: NormalizedLibraryProject = typeof projectOrId === 'string'
      ? {
          id: projectOrId,
          source: collection === 'videos'
            ? { type: 'legacy-video', storagePath, size: 0 }
            : { type: 'upload', storagePath, uploadSize: 0 },
          title: title || '',
          segments: [],
          settings: {
            defaultSpeed: 1,
            defaultMirror: true,
            autoLoop: false,
            transitionType: 'none'
          },
          createdAt: null,
          updatedAt: null
        }
      : projectOrId;
    const currentStoragePath = getLibraryProjectStoragePath(project);

    setCurrentVideoId(project.id);
    setCurrentStoragePath(currentStoragePath || null);
    setIsSavedToLibrary(true);
    setLoadedFromLibraryId(project.id);
    setLoadedLibraryProject(project);
    setVideoTitle(project.title || title || '');

    if (!sharingEnabled) {
      setShareUrl('');
      setShareExpiresAt(null);
      setHasActiveShare(false);
      return;
    }
    
    // Check if this video has an active share
    const existing = await checkExistingShare(project.id);
    if (existing.hasShare) {
      setShareUrl(existing.shareUrl || '');
      setShareExpiresAt(existing.expiresAt || null);
      setHasActiveShare(true);
    } else {
      setHasActiveShare(false);
    }
  };

  /**
   * Reset library state (when new video is loaded)
   */
  const resetLibraryState = () => {
    activeAutoSaveKeyRef.current = null;
    setIsSavedToLibrary(false);
    setCurrentVideoId(null);
    setCurrentStoragePath(null);
    setLoadedFromLibraryId(null);
    setLoadedLibraryProject(null);
    setHasActiveShare(false);
    setShareUrl('');
    setShareExpiresAt(null);
    setVideoTitle('');
    setAutoSaveStatus('idle');
  };

  /**
   * Close share modal
   */
  const closeShareModal = () => {
    setShowShareModal(false);
  };

  /**
   * Copy share URL to clipboard
   */
  const copyShareUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      return true;
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      return false;
    }
  };

  // Once a guest signs in, copy local projects to their cloud library. Each
  // IndexedDB record remains in place until its corresponding cloud save has
  // completed, so a failed upload cannot erase the guest's work.
  useEffect(() => {
    const uid = userProfile?.uid;
    if (!isAuthenticated || !uid || typeof window === 'undefined' || !window.indexedDB) return;
    if (migrationAttemptedForUserRef.current.has(uid)) return;
    migrationAttemptedForUserRef.current.add(uid);

    let cancelled = false;
    void migrateGuestLibraryToAccount().then(async result => {
      if (cancelled) return;

      const migratedCurrent = loadedLibraryProject
        ? result.migrated.find(item => item.localId === loadedLibraryProject.id)
        : undefined;
      if (migratedCurrent) {
        const project = migratedCurrent.cloudProject;
        setCurrentVideoId(project.id);
        setCurrentStoragePath(getLibraryProjectStoragePath(project) || null);
        setLoadedFromLibraryId(project.id);
        setLoadedLibraryProject(project);
        setVideoTitle(project.title);
        setIsSavedToLibrary(true);
      }

      if (result.migrated.length > 0) {
        await refreshProfile();
      }
      if (result.failed.length > 0) {
        console.warn('Some device-only library items could not be backed up yet:', result.failed);
      }
    }).catch(error => {
      console.warn('Guest library migration could not start:', error);
    });

    return () => {
      cancelled = true;
    };
    // The migration runs once per signed-in user. The current project values
    // are intentionally captured at the authentication transition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, userProfile?.uid]);

  /**
   * Helper functions for button states
   */
  const canSaveToLibrary = !isSavedToLibrary &&
                           !loadedFromLibraryId;

  const canShare = sharingEnabled &&
                   isAuthenticated &&
                   isSavedToLibrary && 
                   currentVideoId !== null &&
                   Boolean(currentStoragePath);

  const canUpdateSegments = loadedLibraryProject !== null && (
    isAuthenticated || isGuestLibraryProject(loadedLibraryProject)
  );

  const getSaveButtonTooltip = (hasVideo: boolean, hasSegments: boolean) => {
    if (!hasVideo) return "Upload your own video (demos cannot be saved)";
    if (!hasSegments) return "Create segments first";
    if (isSavedToLibrary) return "Already saved to library";

    if (!isAuthenticated) return "Save to this device";
    if (!SHOW_UPLOAD_SLOT_LIMIT_UI) return "Save to library";

    const slotsUsed = userProfile?.libraryCount || 0;
    const slotsTotal = userProfile?.isPremium ? '∞' : String(FREE_UPLOAD_PROJECT_LIMIT);

    return `Save to library (${slotsUsed}/${slotsTotal} Croop Free slots used)`;
  };

  const getShareButtonTooltip = () => {
    if (!sharingEnabled) return "Sharing is unavailable";
    if (!isAuthenticated) return "Sign in to share";
    if (!isSavedToLibrary) return "Save to library first";
    if (!currentStoragePath) return "Sharing is available for uploaded videos only";
    return `Create share link (${SHARE_LIMITS.MAX_VIEWS_PER_SHARE} views, ${SHARE_LIMITS.SHARE_EXPIRY_DAYS} days, ${SHARE_LIMITS.MAX_ACTIVE_SHARES_PER_USER} active)`;
  };

  const getUpdateButtonTooltip = () => {
    if (!loadedFromLibraryId) return "Save to library first";
    if (!isAuthenticated && loadedLibraryProject && !isGuestLibraryProject(loadedLibraryProject)) {
      return "Sign in again to update this cloud item";
    }
    return "Update segments in library";
  };

  return {
    // State
    currentVideoId,
    currentStoragePath,
    videoTitle,
    isSavedToLibrary,
    showShareModal,
    shareUrl,
    shareExpiresAt,
    loadedFromLibraryId,
    loadedLibraryProject,
    hasActiveShare,
    autoSaveStatus,
    setAutoSaveStatus,

    // Actions
    handleSaveToLibrary,
    handleSaveYouTubeProject,
    handleUpdateSegments,
    handleShareSession,
    handleCreateNewShare,
    resetLibraryState,
    closeShareModal,
    copyShareUrl,
    setLoadedFromLibrary,
    checkExistingShare,
    
    // Helpers
    canSaveToLibrary,
    canShare,
    canUpdateSegments,
    getSaveButtonTooltip,
    getShareButtonTooltip,
    getUpdateButtonTooltip
  };
};
